import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import PageMeta from "@/components/PageMeta";
import { ArrowRight, Briefcase, Users, FileLock, Building2, ShieldCheck, LineChart } from "lucide-react";

const COPY = {
  de: {
    eyebrow: "Leistungen · 04",
    h1a: "Investoren",
    h1b_em: "&",
    h1b: "Projektpartner.",
    lede: "Wir arbeiten mit Family Offices, institutionellen Co-Investoren und langfristig orientierten Projektpartnern an ausgewählten Wohn- und Mischnutzungsprojekten in Wien, Niederösterreich und unseren ukrainischen Standorten.",

    intro_title: "Strukturierte Zusammenarbeit.",
    intro_body: "Wir entwickeln je Projekt eine klare Investitionsstruktur — projektbezogene Gesellschaft, Beteiligungsmodell, Reporting. Keine Pauschalversprechen, keine garantierten Renditen. Was wir liefern: saubere Planung, kontrollierte Bauzeit, dokumentierte Qualität.",

    profile_title: "Wir passen, wenn Sie …",
    profile: [
      { icon: Briefcase, no: "01", title: "langfristig denken", text: "Wertentwicklung über Bauzeit und Vermarktung, nicht über kurzfristige Spekulation." },
      { icon: ShieldCheck, no: "02", title: "Substanz statt Story suchen", text: "Klare Standorte, durchgeplante Projekte, dokumentierte Qualität — nicht Pitch-Decks." },
      { icon: LineChart, no: "03", title: "Reporting erwarten", text: "Quartalsweise Bau- und Finanzreports, klar nachvollziehbare KPIs, transparente Mittelverwendung." },
      { icon: Users, no: "04", title: "auf Augenhöhe arbeiten", text: "Persönliche Betreuung durch die Geschäftsführung, kein anonymisierter Vertrieb." },
    ],

    formats_title: "Beteiligungsformate.",
    formats: [
      { title: "Projektbezogene Beteiligung", text: "Eigenkapital-Beteiligung an einer einzelnen Projektgesellschaft (SPV). Klar definierter Projektzeitraum, Exit nach Vermarktung." },
      { title: "Mezzanine-Finanzierung", text: "Nachrangige Finanzierung mit fester Verzinsung und definierter Laufzeit, projektbezogen besichert." },
      { title: "Joint Venture Equity", text: "Längerfristige Partnerschaft über mehrere Projekte mit gemeinsamer Steuerung." },
      { title: "Stille Beteiligung", text: "Diskrete Beteiligungsstruktur ohne operative Einflussnahme, mit klarer Renditesystematik." },
    ],

    governance_title: "Reporting & Governance.",
    governance: [
      { no: "01", title: "Projektgesellschaft", text: "Jede Beteiligung läuft über eine eigene Zweck-GmbH (SPV) mit klar abgegrenztem Wirtschaftskreislauf." },
      { no: "02", title: "Quartalsberichte", text: "Baufortschritt, Liquiditätsstatus, Vermarktungsstand, Risikoeinschätzung — schriftlich, dokumentiert." },
      { no: "03", title: "Externe Prüfer", text: "Jahresabschlüsse und Bauabrechnungen werden extern geprüft." },
      { no: "04", title: "Beirat / Ausschuss", text: "Auf Wunsch repräsentative Mitsprache bei wesentlichen Projektentscheidungen über einen Beirat." },
    ],

    note_title: "Wichtiger Hinweis.",
    note_body: "Beteiligungen an Immobilienprojekten sind keine garantierten Anlageprodukte. Es bestehen Markt-, Bau-, Finanzierungs- und Vermarktungsrisiken bis hin zum Totalverlust. Eine konkrete Beteiligung erfolgt ausschließlich auf Basis der projektbezogenen rechtlichen Dokumentation. Diese Seite ist eine Vorab-Information und stellt kein Angebot dar.",

    cta_title: "Erstes Gespräch?",
    cta_body: "Vereinbaren Sie ein vertrauliches Gespräch mit der Geschäftsführung. Wir teilen anschließend konkrete Projektunterlagen unter NDA.",
    cta_btn: "Gespräch anfragen",
  },
  en: {
    eyebrow: "Services · 04",
    h1a: "Investors",
    h1b_em: "&",
    h1b: "Project Partners.",
    lede: "We work with family offices, institutional co-investors, and long-term oriented project partners on selected residential and mixed-use projects in Vienna, Lower Austria, and our Ukrainian locations.",
    intro_title: "Structured Collaboration.",
    intro_body: "For each project, we develop a clear investment structure — project-specific company, participation model, reporting. No blanket promises, no guaranteed returns. What we deliver: meticulous planning, controlled construction time, documented quality.",
    profile_title: "We are a good fit if you …",
    profile: [
      { icon: Briefcase, no: "01", title: "think long-term", text: "Value development over construction time and marketing, not through short-term speculation." },
      { icon: ShieldCheck, no: "02", title: "seek substance over story", text: "Clear locations, thoroughly planned projects, documented quality — not just pitch decks." },
      { icon: LineChart, no: "03", title: "expect reporting", text: "Quarterly construction and financial reports, clearly traceable KPIs, transparent use of funds." },
      { icon: Users, no: "04", title: "work at eye level", text: "Personal support from management, no anonymized sales." },
    ],
    formats_title: "Participation Formats.",
    formats: [
      { title: "Project-Specific Participation", text: "Equity participation in a single project company (SPV). Clearly defined project period, exit after marketing." },
      { title: "Mezzanine Financing", text: "Subordinated financing with fixed interest and defined term, project-specifically secured." },
      { title: "Joint Venture Equity", text: "Longer-term partnership across multiple projects with joint management." },
      { title: "Silent Partnership", text: "Discrete participation structure without operational influence, with a clear return system." },
    ],
    governance_title: "Reporting & Governance.",
    governance: [
      { no: "01", title: "Project Company", text: "Each participation runs through its own special purpose GmbH (SPV) with a clearly defined economic cycle." },
      { no: "02", title: "Quarterly Reports", text: "Construction progress, liquidity status, marketing status, risk assessment — written, documented." },
      { no: "03", title: "External Auditors", text: "Annual financial statements and construction accounts are externally audited." },
      { no: "04", title: "Advisory Board / Committee", text: "Upon request, representative co-determination in key project decisions through an advisory board." },
    ],
    note_title: "Important Notice.",
    note_body: "Participations in real estate projects are not guaranteed investment products. There are market, construction, financing, and marketing risks, up to total loss. Concrete participation occurs exclusively on the basis of project-specific legal documentation. This page is preliminary information and does not constitute an offer.",
    cta_title: "First Discussion?",
    cta_body: "Arrange a confidential meeting with management. We will then share specific project documents under NDA.",
    cta_btn: "Request Meeting",
  },
  ru: {
    eyebrow: "Услуги · 04",
    h1a: "Инвесторы",
    h1b_em: "&",
    h1b: "проектные партнеры.",
    lede: "Мы работаем с семейными офисами, институциональными соинвесторами и долгосрочно ориентированными проектными партнерами над выбранными жилыми и многофункциональными проектами в Вена, Нижней Австрии и наших украинских локациях.",
    intro_title: "Структурированное сотрудничество.",
    intro_body: "Для каждого проекта мы разрабатываем четкую инвестиционную структуру — проектная компания, модель участия, отчетность. Никаких общих обещаний, никаких гарантированных доходностей. Что мы предоставляем: тщательное планирование, контролируемые сроки строительства, документированное качество.",
    profile_title: "Мы подходим, если вы …",
    profile: [
      { icon: Briefcase, no: "01", title: "думаете долгосрочно", text: "Рост стоимости на протяжении всего срока строительства и маркетинга, а не через краткосрочные спекуляции." },
      { icon: ShieldCheck, no: "02", title: "ищете субстанцию, а не историю", text: "Четкие локации, тщательно спланированные проекты, документированное качество — не просто питч-деки." },
      { icon: LineChart, no: "03", title: "ожидаете отчетность", text: "Ежеквартальные отчеты о ходе строительства и финансовые отчеты, четко отслеживаемые KPI, прозрачное использование средств." },
      { icon: Users, no: "04", title: "работаете на равных", text: "Персональное сопровождение руководством, никаких анонимных продаж." },
    ],
    formats_title: "Форматы участия.",
    formats: [
      { title: "Проектное участие", text: "Долевое участие в уставном капитале отдельной проектной компании (SPV). Четко определенный период проекта, выход после маркетинга." },
      { title: "Мезонинное финансирование", text: "Субординированное финансирование с фиксированной процентной ставкой и определенным сроком, обеспеченное проектно." },
      { title: "Совместное предприятие (акции)", text: "Долгосрочное партнерство по нескольким проектам с совместным управлением." },
      { title: "Скрытое участие", text: "Дискретная структура участия без оперативного влияния, с четкой систематикой доходности." },
    ],
    governance_title: "Отчетность и управление.",
    governance: [
      { no: "01", title: "Проектная компания", text: "Каждое участие осуществляется через собственную целевую GmbH (SPV) с четко разграниченным экономическим циклом." },
      { no: "02", title: "Квартальные отчеты", text: "Ход строительства, состояние ликвидности, статус маркетинга, оценка рисков — письменно, документально." },
      { no: "03", title: "Внешние аудиторы", text: "Годовая финансовая отчетность и строительные расчеты проверяются внешними аудиторами." },
      { no: "04", title: "Консультативный совет / Комитет", text: "По желанию, представительное участие в ключевых проектных решениях через консультативный совет." },
    ],
    note_title: "Важное примечание.",
    note_body: "Участие в проектах недвижимости не является гарантированным инвестиционным продуктом. Существуют рыночные, строительные, финансовые и маркетинговые риски, вплоть до полной потери капитала. Конкретное участие осуществляется исключительно на основе проектно-специфической юридической документации. Эта страница является предварительной информацией и не представляет собой предложение.",
    cta_title: "Первая беседа?",
    cta_body: "Договоритесь о конфиденциальной встрече с руководством. Затем мы предоставим конкретные проектные документы под соглашением о неразглашении (NDA).",
    cta_btn: "Запросить встречу",
  },
  uk: {
    eyebrow: "Послуги · 04",
    h1a: "Інвестори",
    h1b_em: "&",
    h1b: "проектні партнери.",
    lede: "Ми працюємо з сімейними офісами, інституційними співінвесторами та довгостроково орієнтованими проектними партнерами над обраними житловими та багатофункціональними проектами у Відні, Нижній Австрії та наших українських локаціях.",
    intro_title: "Структурована співпраця.",
    intro_body: "Для кожного проекту ми розробляємо чітку інвестиційну структуру — проектна компанія, модель участі, звітність. Жодних загальних обіцянок, жодних гарантованих прибутків. Що ми надаємо: ретельне планування, контрольований термін будівництва, задокументована якість.",
    profile_title: "Ми підходимо, якщо ви …",
    profile: [
      { icon: Briefcase, no: "01", title: "думаєте довгостроково", text: "Розвиток вартості протягом усього терміну будівництва та маркетингу, а не через короткострокові спекуляції." },
      { icon: ShieldCheck, no: "02", title: "шукаєте субстанцію, а не історію", text: "Чіткі локації, ретельно сплановані проекти, задокументована якість — не просто презентації." },
      { icon: LineChart, no: "03", title: "очікуєте звітність", text: "Квартальні звіти про хід будівництва та фінансові звіти, чітко відстежувані KPI, прозоре використання коштів." },
      { icon: Users, no: "04", title: "працюєте на рівних", text: "Персональна підтримка керівництвом, жодних анонімних продажів." },
    ],
    formats_title: "Формати участі.",
    formats: [
      { title: "Проектна участь", text: "Часткова участь у статутному капіталі окремої проектної компанії (SPV). Чітко визначений період проекту, вихід після маркетингу." },
      { title: "Мезонінне фінансування", text: "Субординоване фінансування з фіксованою відсотковою ставкою та визначеним терміном, забезпечене проектно." },
      { title: "Спільне підприємство (капітал)", text: "Довгострокове партнерство за кількома проектами зі спільним управлінням." },
      { title: "Прихована участь", text: "Дискретна структура участі без оперативного впливу, з чіткою систематикою прибутковості." },
    ],
    governance_title: "Звітність та управління.",
    governance: [
      { no: "01", title: "Проектна компанія", text: "Кожна участь здійснюється через власну цільову GmbH (SPV) з чітко розмежованим економічним циклом." },
      { no: "02", title: "Квартальні звіти", text: "Хід будівництва, стан ліквідності, статус маркетингу, оцінка ризиків — письмово, задокументовано." },
      { no: "03", title: "Зовнішні аудитори", text: "Річна фінансова звітність та будівельні розрахунки перевіряються зовнішніми аудиторами." },
      { no: "04", title: "Наглядова рада / Комітет", text: "За бажанням, представницька участь у ключових проектних рішеннях через наглядову раду." },
    ],
    note_title: "Важливе зауваження.",
    note_body: "Участь у проектах нерухомості не є гарантованим інвестиційним продуктом. Існують ринкові, будівельні, фінансові та маркетингові ризики, аж до повної втрати капіталу. Конкретна участь здійснюється виключно на основі проектно-специфічної юридичної документації. Ця сторінка є попередньою інформацією і не становить пропозиції.",
    cta_title: "Перша розмова?",
    cta_body: "Домовтеся про конфіденційну зустріч з керівництвом. Потім ми надамо конкретні проектні документи під угодою про нерозголошення (NDA).",
    cta_btn: "Запитати зустріч",
  },
};

export default function InvestorenPage() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.de;

  return (
    <div data-testid="investoren-page">
      <PageMeta pageKey="investoren" />

      <section className="relative pt-32 pb-24 md:pt-44 md:pb-32 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex items-center gap-4 mb-10">
          <span className="w-12 h-px bg-white/55" />
          <span className="text-[11px] tracking-[0.32em] uppercase text-white/65">{c.eyebrow}</span>
        </div>
        <h1 className="font-display font-light text-5xl sm:text-6xl md:text-7xl lg:text-8xl uppercase tracking-tighter leading-[0.95] break-words hyphens-auto">
          {c.h1a} <span className="italic font-extralight">{c.h1b_em}</span>
          <br />
          {c.h1b}
        </h1>
        <p className="mt-10 max-w-2xl text-base md:text-lg text-white/80 leading-[1.65] font-light">{c.lede}</p>
      </section>

      <section className="py-20 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 grid md:grid-cols-12 gap-10 lg:gap-20 border-t border-white/10">
        <div className="md:col-span-5 min-w-0">
          <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-5">01</p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] break-words hyphens-auto">
            {c.intro_title}
          </h2>
        </div>
        <div className="md:col-span-7 md:pt-8 text-white/80 text-base md:text-lg leading-[1.7] font-light">
          <p>{c.intro_body}</p>
        </div>
      </section>

      <section className="py-24 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 border-t border-white/10">
        <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-3">02</p>
        <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-16 break-words hyphens-auto">
          {c.profile_title}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
          {c.profile.map((it) => (
            <div key={it.no} className="bg-[#051630] p-8 md:p-10">
              <div className="flex items-baseline justify-between mb-5">
                <it.icon size={22} strokeWidth={1.2} className="text-white/70" />
                <span className="text-[10px] tracking-[0.3em] uppercase text-white/40">{it.no}</span>
              </div>
              <h3 className="font-display text-lg uppercase tracking-tight mb-3">{it.title}</h3>
              <p className="text-sm text-white/70 leading-[1.7] font-light">{it.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 border-t border-white/10">
        <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-3">03</p>
        <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-16">
          {c.formats_title}
        </h2>
        <div className="grid sm:grid-cols-2 gap-px bg-white/10">
          {c.formats.map((t) => (
            <div key={t.title} className="bg-[#051630] p-8 md:p-10">
              <Building2 size={22} strokeWidth={1.2} className="text-white/60 mb-4" />
              <h3 className="font-display text-xl uppercase tracking-tight mb-3">{t.title}</h3>
              <p className="text-sm text-white/70 leading-[1.7] font-light">{t.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 border-t border-white/10">
        <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-3">04</p>
        <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-16">
          {c.governance_title}
        </h2>
        <div className="space-y-px bg-white/10">
          {c.governance.map((s) => (
            <div key={s.no} className="bg-[#051630] grid md:grid-cols-12 gap-6 px-6 md:px-10 py-8">
              <div className="md:col-span-2 text-[11px] tracking-[0.3em] uppercase text-white/45">{s.no}</div>
              <div className="md:col-span-3 font-display text-lg md:text-xl uppercase tracking-tight">{s.title}</div>
              <p className="md:col-span-7 text-white/75 leading-[1.7] font-light">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 border-t border-white/10">
        <div className="border-l-2 border-white/30 pl-8 max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <FileLock size={16} strokeWidth={1.5} className="text-white/55" />
            <p className="text-[11px] tracking-[0.3em] uppercase text-white/55">{c.note_title}</p>
          </div>
          <p className="text-white/65 text-sm leading-[1.75] font-light">{c.note_body}</p>
        </div>
      </section>

      <section className="py-24 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 border-t border-white/10 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7 min-w-0">
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] break-words hyphens-auto">
            {c.cta_title}
          </h2>
          <p className="mt-6 max-w-xl text-white/75 leading-[1.7] font-light">{c.cta_body}</p>
        </div>
        <div className="md:col-span-5 flex md:justify-end md:items-end">
          <Link
            to="/kontakt"
            data-testid="investoren-cta"
            className="group inline-flex items-center gap-6 bg-white text-[#051630] font-semibold tracking-[0.2em] uppercase text-[11px] px-8 py-5 border border-white hover:bg-transparent hover:text-white transition-colors"
          >
            <span>{c.cta_btn}</span>
            <ArrowRight size={16} strokeWidth={2} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
